'use client';

/**
 * URL editor for iframe-external content widgets.
 *
 * Lives behind the "Edit URL" toggle on the content-node toolbar.
 * The editor:
 *
 *   1. Shows the current `metadata.bundle.entryUrl` (what the iframe
 *      is actually rendering).
 *   2. Lets the user paste a new URL, runs it through
 *      `lookupIframeEmbed()` for known-host transformation, and
 *      shows what the iframe will load.
 *   3. POSTs the new URL via the existing PATCH /api/content/[id]
 *      endpoint (server-side SSRF re-validation runs there).
 *   4. Surfaces an "Open original" affordance so users can click
 *      through to the auth'd surface in a new tab — the escape
 *      hatch for embeds that are auth-walled inside credentialless
 *      iframes (e.g. private Figma files).
 *
 * Keep the surface minimal — this isn't a settings panel, just an
 * inline URL fix-up for the most common "wrong URL / want to tweak
 * params" flow.
 */

import { useCallback, useMemo, useState } from 'react';
import { useT } from '@/components/i18n-provider';
import { ExternalLink, RotateCw, Save } from 'lucide-react';
import { cn } from '@/lib/utils';
import { lookupIframeEmbed } from '@/lib/content/iframe-embeds';

export interface ExternalSourceEditorProps {
  contentId: string;
  currentEntryUrl: string;
  /** Called after a successful save with the new URL the iframe is now rendering. */
  onSaved: (newEntryUrl: string) => void;
}

type Status = 'idle' | 'saving' | 'saved' | 'error';

export function ExternalSourceEditor({
  contentId,
  currentEntryUrl,
  onSaved,
}: ExternalSourceEditorProps) {
  const t = useT();
  const [draft, setDraft] = useState<string>(currentEntryUrl);
  const [status, setStatus] = useState<Status>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // What the iframe will actually load if the user hits Save with
  // the current draft. Registry hit → transformed URL (e.g.
  // `youtube.com/watch?v=X` becomes `youtube.com/embed/X`). Miss →
  // the raw draft URL.
  const resolved = useMemo(() => {
    const trimmed = draft.trim();
    if (!trimmed) return { embedUrl: '', siteName: null as string | null };
    const hit = lookupIframeEmbed(trimmed);
    if (hit) return { embedUrl: hit.embedUrl, siteName: hit.siteName };
    return { embedUrl: trimmed, siteName: null };
  }, [draft]);

  const dirty = resolved.embedUrl !== currentEntryUrl && resolved.embedUrl !== '';
  const willTransform = resolved.embedUrl !== draft.trim() && resolved.embedUrl !== '';

  const onSave = useCallback(async () => {
    if (!dirty || status === 'saving') return;
    setStatus('saving');
    setErrorMessage(null);
    try {
      const res = await fetch(`/api/content/${contentId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ bundleEntryUrl: resolved.embedUrl }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setStatus('error');
        setErrorMessage(
          body?.error || `Update failed (${res.status})`,
        );
        return;
      }
      setStatus('saved');
      onSaved(resolved.embedUrl);
      // Visual flash — back to idle after a moment so the next edit
      // is unambiguous.
      window.setTimeout(() => setStatus('idle'), 1500);
    } catch (err) {
      setStatus('error');
      setErrorMessage(err instanceof Error ? err.message : 'Network error');
    }
  }, [contentId, dirty, resolved.embedUrl, status, onSaved]);

  const openInNewTab = useCallback(() => {
    if (!currentEntryUrl) return;
    window.open(currentEntryUrl, '_blank', 'noopener,noreferrer');
  }, [currentEntryUrl]);

  return (
    <div className="flex h-full w-full flex-col gap-3 p-4 text-sm">
      <label className="flex min-h-0 flex-1 flex-col gap-1">
        <span className="shrink-0 text-xs font-medium uppercase tracking-wider text-muted-foreground">
          {t('externalSource.embedUrl')}
        </span>
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          spellCheck={false}
          // `flex-1` + `min-h-0` lets the textarea grow to fill the
          // remaining vertical space inside the node body. Long URLs
          // (Figma embeds run 400+ chars) get plenty of room without
          // the user having to drag the resize handle. `resize-none`
          // because the flex container is the size authority.
          className={cn(
            'min-h-0 w-full flex-1 resize-none rounded-md border border-border bg-background px-2 py-1.5 font-mono text-xs',
            'focus:outline-none focus:ring-2 focus:ring-primary/30',
          )}
          placeholder="https://…"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              void onSave();
            }
          }}
        />
      </label>

      {willTransform && (
        <div className="rounded-md border border-amber-500/30 bg-amber-50/40 px-2.5 py-1.5 text-xs text-amber-900 dark:border-amber-500/30 dark:bg-amber-950/30 dark:text-amber-100">
          <div className="font-medium">
            Will load as {resolved.siteName ?? 'iframe-friendly URL'}
          </div>
          <div className="mt-1 break-all font-mono text-[11px] opacity-80">
            {resolved.embedUrl}
          </div>
        </div>
      )}

      {errorMessage && (
        <div className="rounded-md border border-rose-500/30 bg-rose-50/40 px-2.5 py-1.5 text-xs text-rose-900 dark:border-rose-500/30 dark:bg-rose-950/30 dark:text-rose-100">
          {errorMessage}
        </div>
      )}

      <div className="mt-1 flex items-center gap-2">
        <button
          type="button"
          onClick={() => void onSave()}
          disabled={!dirty || status === 'saving'}
          className={cn(
            'inline-flex items-center gap-1.5 rounded-md border border-primary/30 bg-primary/10 px-2.5 py-1 text-xs font-medium',
            'text-primary hover:bg-primary/20',
            'disabled:opacity-50 disabled:hover:bg-primary/10',
          )}
          title={dirty ? 'Save and reload the iframe' : t('externalSource.noChanges')}
        >
          {status === 'saving' ? (
            <>
              <RotateCw className="h-3 w-3 animate-spin" />
              Saving…
            </>
          ) : status === 'saved' ? (
            <>
              <Save className="h-3 w-3" />
              Saved
            </>
          ) : (
            <>
              <Save className="h-3 w-3" />
              {t('externalSource.saveAndReload')}
            </>
          )}
        </button>

        <button
          type="button"
          onClick={openInNewTab}
          disabled={!currentEntryUrl}
          className={cn(
            'inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-2.5 py-1 text-xs font-medium',
            'text-muted-foreground hover:bg-accent hover:text-foreground',
            'disabled:opacity-50',
          )}
          // Escape hatch for embeds that are auth-walled inside the
          // credentialless iframe (private Figma files, signed-in
          // Notion pages). Opening in a new tab puts the user back
          // in their normal browsing context with full session
          // state.
          title="Open the original URL in a new tab (uses your normal browser session)"
        >
          <ExternalLink className="h-3 w-3" />
          {t('externalSource.openOriginal')}
        </button>
      </div>
    </div>
  );
}
